import {produce} from 'immer';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {BioentitySummary, TargetStorable} from 'folder-items/FileConstants';
import {
  StructuredTableKind,
  StructuredTableRow,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Cell} from 'notebook/tables/TableConstants';
import {toStorableTypedValueCell, toBioentityTypedValueCell} from 'notebook/tables/typed-values/DataTypes';
import {StorableLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableRowMetadata,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

function makeNewRowWithCell({
  table,
  cell,
  rowNum,
  colNum,
  container,
}: {
  table: ProcessTaskStructuredTable;
  cell: Cell | null;
  rowNum: number;
  colNum: number;
  container: TargetStorable | null;
}): StructuredTableRow<ProcessTaskStructuredTableRowMetadata> | null {
  assert(colNum >= 0, 'Unexpected column index when creating new row', {colNum, table});
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  const blankRow = adapter.getBlankRow(table.metadata, rowNum, table.columns);
  if (container != null) {
    const containerTypedValue: StorableLinkSchemaFieldTypedValue = {
      type: FieldType.STORABLE_LINK,
      value: '',
      targetStorables: [
        {
          id: container.id,
          name: container.name,
          iconId: container.iconId,
        },
      ],
    };
    return produce(blankRow, (draftBlankRow) => {
      draftBlankRow.cells[colNum].typedValue = containerTypedValue;
      return draftBlankRow;
    });
  } else if (cell != null) {
    return produce(blankRow, (draftBlankRow) => {
      draftBlankRow.cells[colNum] = cell;
      return draftBlankRow;
    });
  }
  return null;
}

export type StorableSummaryWithContainer = MinimalStorableSummaryWithSchemaId & {
  container?: TargetStorable | null;
};

export function propagateItemsToDestinationTable({
  items,
  destinationColumnIndex,
  destinationTable,
}: {
  items: Array<BioentitySummary> | Array<StorableSummaryWithContainer>;
  destinationColumnIndex: number;
  destinationTable: ProcessTaskStructuredTable;
}): ProcessTaskStructuredTable {
  const isStorable = (
    item: BioentitySummary | MinimalStorableSummaryWithSchemaId
  ): item is MinimalStorableSummaryWithSchemaId => APIUtil.isStorable(item.id);

  const updatedTable = produce(destinationTable, (draftTable) => {
    const newRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> = [];
    items.forEach((item, idx) => {
      const cell = isStorable(item) ? toStorableTypedValueCell([item]) : toBioentityTypedValueCell(item);
      const newRow = makeNewRowWithCell({
        table: destinationTable,
        cell,
        rowNum: idx,
        colNum: destinationColumnIndex,
        container: isStorable(item) ? (item.container ?? null) : null,
      });
      if (newRow != null) {
        newRows.push(newRow);
      }
    });
    if (newRows.length > 0) {
      draftTable.rows = newRows;
    }
  });
  return updatedTable;
}

export default function propagateContentFromSourceToDestinationTable({
  sourceColumnIndex,
  sourceTable,
  sourceComputedTable,
  destinationColumnIndex,
  destinationTable,
  containers,
}: {
  sourceColumnIndex: number;
  sourceTable: UnknownStructuredTable;
  sourceComputedTable?: Array<Array<EvaluatedCell>> | null;
  destinationColumnIndex: number;
  destinationTable: ProcessTaskStructuredTable;
  containers: Array<TargetStorable> | null;
}): ProcessTaskStructuredTable {
  return produce(destinationTable, (draftTable) => {
    const newRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> = [];
    for (let rowNum = 0; rowNum < sourceTable.rows.length; rowNum++) {
      const sourceRow = sourceTable.rows[rowNum] ?? {};
      const computedValue = sourceComputedTable?.[rowNum][sourceColumnIndex]?.value;
      const newRow = makeNewRowWithCell({
        table: destinationTable,
        cell: computedValue
          ? {text: '', typedValue: computedValue}
          : (sourceRow.cells?.[sourceColumnIndex] ?? null),
        rowNum,
        colNum: destinationColumnIndex,
        container: containers?.[rowNum] ?? null,
      });
      if (
        newRow != null &&
        sourceRow.lastSyncedArchivePurpose == null &&
        // Lookup tables' rows are never finalized because they are just data lookups - they do not perform
        // actions that need to be submitted to the server.
        (sourceTable.kind === StructuredTableKind.LOOKUP || sourceRow.isFinalized)
      ) {
        newRows.push(newRow);
      }
    }
    if (newRows.length > 0) {
      draftTable.rows = newRows;
    }
  });
}
